import React, { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Link2 } from 'lucide-react';
import { Button, Modal } from './UIComponents';

interface CopyLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  description?: string;
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

const CopyLinkDialog: React.FC<CopyLinkDialogProps> = ({
  isOpen,
  onClose,
  url,
  title = 'Share link',
  description = 'Anyone with this link can view this page.',
}) => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedUrl = useMemo(() => url.trim(), [url]);

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
    setError(null);
  }, [isOpen, normalizedUrl]);

  const handleCopy = async () => {
    setError(null);
    try {
      await copyToClipboard(normalizedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleCopy} disabled={!normalizedUrl}>
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
            <Link2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-slate-600 dark:text-zinc-400">{description}</p>
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>
        </div>

        <label className="block">
          <span className="sr-only">Share URL</span>
          <input
            type="text"
            readOnly
            value={normalizedUrl}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800 text-sm text-slate-900 dark:text-zinc-100"
          />
        </label>
      </div>
    </Modal>
  );
};

export default CopyLinkDialog;

